import i18next, { type i18n, type Resource } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { type AppLocale, DEFAULT_LOCALE, LOCALE_STORAGE_KEY, SUPPORTED_LOCALES } from './config.js';

export interface CreateI18nOptions {
  /** i18next resource bundle (namespaces × locales). */
  resources: Resource;
  /** Namespace(s) to load. Defaults to 'common'. */
  ns?: string | string[];
  /** Default namespace. Defaults to 'common'. */
  defaultNS?: string;
  /** Locale to fall back to when detection fails. Defaults to DEFAULT_LOCALE. */
  fallbackLng?: AppLocale;
}

/**
 * Creates a standalone i18next instance wired to react-i18next, so each app
 * (admin, pwa, visitor) owns its own instance and resource bundle instead of
 * sharing global i18next state.
 */
export function createI18n(options: CreateI18nOptions): i18n {
  const instance = i18next.createInstance();

  instance
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: options.resources,
      ns: options.ns ?? 'common',
      defaultNS: options.defaultNS ?? 'common',
      supportedLngs: [...SUPPORTED_LOCALES],
      fallbackLng: options.fallbackLng ?? DEFAULT_LOCALE,
      load: 'languageOnly',
      nonExplicitSupportedLngs: true,
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ['localStorage', 'navigator', 'htmlTag'],
        lookupLocalStorage: LOCALE_STORAGE_KEY,
        caches: ['localStorage'],
      },
      react: {
        useSuspense: false,
      },
    });

  syncDocumentLanguage(instance);

  return instance;
}

/**
 * Keeps `<html lang>` in step with the active language so screen readers,
 * browser translation prompts, hyphenation and `:lang()` CSS follow what the
 * user actually sees instead of the static value baked into index.html.
 */
function syncDocumentLanguage(instance: i18n): void {
  if (typeof document === 'undefined') return;

  const apply = (): void => {
    const lang = instance.resolvedLanguage ?? instance.language;
    if (lang) document.documentElement.lang = lang;
  };

  apply();
  instance.on('initialized', apply);
  instance.on('languageChanged', apply);
}
